import { GSSidePanelPage, GSForm, GSInput } from "golfstatus_react_components";
import React, { useCallback, useEffect, useState } from "react";
import { useMatch, useNavigate } from "react-router-dom";
import "./tournament-create-edit.scss";
import {
  selectTournamentNotifications,
  setTournamentNotifications,
  selectCurrentTournament,
  setCurrentTournament,
  createTournament,
  saveTournament,
  updateCurrentTournament, getTournamentPointOfContact,
} from "../../reducers/tournamentSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  faChevronLeft,
  faSearch,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import { getFormSections } from "../../forms/TournamentForms";
import { getFormSections as quickStartForm } from "../../forms/TournamentQuickStartForm";
import {
  getOrganizationList,
  saveOrganization,
  selectOrganizationList,
} from "../../reducers/organizationSlice";
import {
  getOrganizationAccessList,
  selectOrganizationMembers,
} from "../../reducers/userSlice";
import {
  clearUtilityNotifications,
  uploadFile,
  selectCroppedTarget,
  selectUploadedFile,
  selectUtilityNotifications,
  clearUtility,
} from "../../reducers/utilitySlice";
import {
  getSaveBannerActions,
  useNotificationAction,
  useNotificationBanner,
} from "../../hooks/notificationHooks";
import {
  useFormDataValidation,
  useImageSelector,
} from "../../hooks/formHooks";
import {selectLoggedInUser} from "../../reducers/appSlice";
import {isOrganizationUser} from "../../helpers/Access";

const TournamentCreateEdit = (props) => {
  // selectors
  const currentUser = useSelector(selectLoggedInUser);
  const currentTournament = useSelector(selectCurrentTournament);
  const organizationList = useSelector(selectOrganizationList);
  const organizationMembers = useSelector(selectOrganizationMembers);
  const tournamentNotifications = useSelector(selectTournamentNotifications);
  const utilNotifications = useSelector(selectUtilityNotifications);
  const croppedTarget = useSelector(selectCroppedTarget);
  const uploadedFile = useSelector(selectUploadedFile);

  // local state
  const [orgName, setOrgName] = useState("");
  const [memberSearch, setMemberSearch] = useState("");
  const [orgFilter, setOrgFilter] = useState({ userID: currentUser.id });
  const [selectedOrganization, setSelectedOrganization] = useState();
  const [selectedPointOfContact, setSelectedPointOfContact] = useState(currentUser);
  const [tosAccepted, setTosAccepted] = useState(false);

  // hooks
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const match = useMatch("/:id/*");
  const settingsMatch = useMatch("/:id/settings/details");
  const [context, isValid, setIsValid] = useFormDataValidation({
    initialValue: false,
    data: currentTournament,
    setData: setCurrentTournament,
  });
  const [, , setLogo, removeLogo, , ,] = useImageSelector(
    context,
    "logo",
    25000000
  );

  // callbacks
  const createTournamentCallback = useCallback(
    () => {
      dispatch(clearUtilityNotifications());
      dispatch(setTournamentNotifications([]));
      if (props?.quickStart === true) {
        navigate(`/tournaments/success/${currentTournament?.id}`);
      } else {
        navigate(`/${currentTournament?.id}`);
      }
    },
    [dispatch, navigate, props?.quickStart, currentTournament?.id]
  );

  const getPointOfContact = useCallback(() => {
    dispatch(getTournamentPointOfContact(currentTournament?.id));
  }, [currentTournament?.id, dispatch]);

  // custom hooks
  useNotificationAction(
    "saved",
    tournamentNotifications,
    createTournamentCallback,
    "Create Tournament"
  );

  useNotificationAction(
    "saved",
    tournamentNotifications,
    getPointOfContact,
    "Tournament"
  );

  // constants
  const firstOrg = organizationList?.[0];
  const deletable = currentTournament?.registrationOrdersCount === 0;

  // effects
  useEffect(() => {
    if (currentTournament?.organization?.id) {
      setSelectedOrganization(currentTournament?.organization);
    }
  }, [currentTournament?.organization]);

  // separate from the org effect so the poc fetch doesn't reset the org and refetch its members
  useEffect(() => {
    if (currentTournament?.pointOfContact?.id) {
      setSelectedPointOfContact(currentTournament?.pointOfContact);
    }
  }, [currentTournament?.pointOfContact]);

  useEffect(() => {
    if (currentUser?.id) {
      dispatch(getOrganizationList({ ...orgFilter, userID: currentUser?.id }));
    }
  }, [orgFilter, currentUser?.id, dispatch]);

  useEffect(() => {
    if (selectedOrganization) {
      dispatch(getOrganizationAccessList(selectedOrganization.id));
    }
  }, [selectedOrganization, dispatch]);

  useEffect(() => {
    if (
      organizationList.length === 1 &&
      selectedOrganization?.id !== firstOrg?.id &&
      currentTournament?.organization?.id === undefined
    ) {
      setSelectedOrganization(firstOrg);
    }
  }, [
    organizationList?.length,
    dispatch,
    firstOrg,
    selectedOrganization?.id,
    currentTournament?.organization?.id,
  ]);

  useEffect(() => {
    if (props?.quickStart === true) {
      const userEmailAddress = currentUser.email;
      const filteredOrganizations = organizationList.filter(
        (organization) => organization.email === userEmailAddress
      );

      const sortedOrganizations = filteredOrganizations.sort(
        (a, b) => new Date(b?.tmsActiveUntil) - new Date(a?.tmsActiveUntil)
      );

      if (sortedOrganizations.length > 0) {
        setSelectedOrganization(sortedOrganizations[0]);
      }
    }
  }, [organizationList, currentUser.email, props?.quickStart]);

  useEffect(() => {
    if (props.quickStart === true) {
      const currentOrgName = selectedOrganization?.name || "";
      const updatedTournament = {
        name: `${new Date().getFullYear()} ${currentOrgName} Golf Tournament`,
        description: `Get ready to tee off at ${currentOrgName}'s golf event! Every swing supports ${currentOrgName}'s mission, so join us for good vibes and a great cause. Register early, be part of the fun, and let’s do good together!`,
      };
      dispatch(updateCurrentTournament(updatedTournament));
    }
  }, [selectedOrganization?.name, dispatch, props?.quickStart]);

  useEffect(() => {
    if (croppedTarget?.logo && selectedOrganization) {
      setIsValid(true);
    }
  }, [croppedTarget?.logo, selectedOrganization, setIsValid]);

  // page helper methods
  const clearName = () => {
    setOrgName("");
    setOrgFilter({ userID: currentUser.id });
  };
  const changeName = (e) => {
    setOrgName(e.target.value);
  };
  const submitSearch = () => {
    setOrgFilter({ ...orgFilter, name: orgName });
  };
  const clearMemberSearch = () => {
    setMemberSearch("");
  };
  const changeMemberSearch = (e) => {
    setMemberSearch(e.target.value);
  };

  const accept = () => {
    let update = { ...selectedOrganization };
    update.tosAcceptedAt = new Date();
    update.tosAcceptedBy = currentUser.id;
    dispatch(saveOrganization(update));
  };

  // setup context
  context.clearTournamentName = () => {
    dispatch(
      setCurrentTournament({
        ...currentTournament,
        name: "",
      })
    );
  };

  context.tosAccepted = tosAccepted;
  context.isOrganizationUser = isOrganizationUser(currentUser, selectedOrganization);
  context.isOrganizationSearchDisabled = organizationList?.length === 0 && orgName === "";

  context.toggleAccepted = () => {
    setIsValid(true);
    setTosAccepted(!tosAccepted);
  };

  context.updateTournament = (value, property) => {
    setIsValid(true);
    let tourney = { ...currentTournament };
    tourney[property] = value;
    return dispatch(setCurrentTournament(tourney));
  };

  context.getOrganizationSearch = () => {
    return (
      <div className="organization-search">
        <GSInput
          textValue={orgName}
          leftIcon={faSearch}
          rightIconClick={clearName}
          rightIcon={faTimesCircle}
          onChange={changeName}
          onSubmit={submitSearch}
          placeholder="Organization Name"
        ></GSInput>
      </div>
    );
  };

  context.getMemberSearch = () => {
    return (
      <div className="member-search">
        <GSInput
          textValue={memberSearch}
          leftIcon={faSearch}
          rightIconClick={clearMemberSearch}
          rightIcon={faTimesCircle}
          onChange={changeMemberSearch}
          placeholder="Member Name"
        ></GSInput>
      </div>
    );
  };
  context.getOrganizationList = () => {
    return organizationList?.filter?.((org) =>
      org?.name?.toLowerCase?.().includes?.(orgName?.toLowerCase?.())
    );
  };
  context.getSelectedOrganization = () => {
    return selectedOrganization;
  };
  context.selectOrganization = (organization) => {
    if (!organization.tosAcceptedAt && !props.quickStart) {
      window.open(
        `${process.env.REACT_APP_GOLFSTATUS_ORGANIZATIONS_URL}/${organization.id}`
      );
      return;
    }
    setIsValid(true);
    // switching organizations means different members, assign the only user we know belongs to both
    if (organization?.id !== selectedOrganization?.id) {
      setSelectedPointOfContact(currentUser);
    }
    setSelectedOrganization(organization);
  };
  context.removeSelectedOrganization = () => {
    setSelectedOrganization(false);
  };
  context.toggleTournamentWaitlist = () => {
    context.updateTournament(!currentTournament.hasWaitList, "hasWaitList");
  };
  context.togglePreflight = () => {
    context.updateTournament(!currentTournament.preflight, "preflight");
  };
  context.toggleHandicapScoring = () => {
    context.updateTournament(
      !currentTournament.handicapScoring,
      "handicapScoring"
    );
  };
  context.toggleUsePlayerAverageHandicap = () => {
    context.updateTournament(
      !currentTournament.usePlayerAverageHandicap,
      "usePlayerAverageHandicap"
    );
  };
  context.getOrganizationMembers = () => {
    return [...organizationMembers]?.filter?.((member) =>
      member.user.invitationAcceptedAt !== null && member.user.name?.toLowerCase?.().includes(memberSearch?.toLowerCase?.())
    );
  };
  context.getSelectedPointOfContact = () => {
    return selectedPointOfContact ?? currentUser;
  };
  context.selectPointOfContact = (member) => {
    setIsValid(true);
    setSelectedPointOfContact(member?.user ?? member);
  };
  context.removePointOfContact = () => {
    setSelectedPointOfContact(false);
  };
  context.validationFailed = () => {
    if (isValid) {
      setIsValid(false);
    }
  };

  // logo
  context.setLogo = setLogo;

  context.removeLogo = removeLogo;

  context.editImage = () => {
    if (props?.quickStart === true) {
      navigate(`../new/edit/image`);
      return;
    }
    if (settingsMatch) {
      navigate(`../edit/image`);
    } else {
      navigate(`./edit/image`);
    }
  };

  context.getLogo = () => {
    if (
      currentTournament.logo &&
      currentTournament.logo !== "/logos/default/missing.png"
    ) {
      return [{ url: currentTournament.logo, type: "image" }];
    }
    if (props?.quickStart === true && !currentTournament.logo) {
      return [
        {
          url: "https://api.golfstatus.dev/images/powered_by_golfstatus.jpg",
          type: "image",
        },
      ];
    }
    return [];
  };

  const uploadImage = (callback) => {
    const data = croppedTarget.logo;
    return dispatch(uploadFile(data));
  };

  const checkFileUpload = () => {
    if (
      croppedTarget?.id === currentTournament?.id &&
      currentTournament?.logo
    ) {
      uploadImage();
    } else {
      createSaveTournament();
    }
  };

  const createSaveTournament = () => {
    let newTournament = { ...currentTournament };
    if (props?.quickStart === true) {
      newTournament.publiclyVisible = true;
      newTournament.registrationOpen = false;
      newTournament.numberOfTeams = "144";
    }
    newTournament.organization = selectedOrganization;
    newTournament.pointOfContact = selectedPointOfContact;
    if (uploadedFile) {
      newTournament.logo = `${process.env.REACT_APP_GOLFSTATUS_BASE_API_URL}${uploadedFile}`;
      dispatch(clearUtility());
    }

    if (newTournament.handicapPercentage) {
      newTournament.adjustHandicaps =
        newTournament.handicapPercentage !== "100";
    }

    if (match.params.id === "tournaments") {
      dispatch(createTournament(newTournament));
    } else {
      dispatch(saveTournament(newTournament));
    }
  };

  const createSaveCallback = useCallback(createSaveTournament, [
    dispatch,
    currentTournament,
    match.params.id,
    selectedOrganization,
    selectedPointOfContact,
    uploadedFile,
    props?.quickStart,
  ]);

  useEffect(() => {
    if ((uploadedFile ?? "") !== "") {
      createSaveCallback();
    }
  }, [uploadedFile, createSaveCallback]);

  const save = () => {
    if (props.quickStart && !selectedOrganization.tosAcceptedAt) {
      accept();
    }
    setUnsaved(false);
    setIsValid(false);
    checkFileUpload();
  };

  const deleteTournament = () => {
    navigate("delete");
  };

  const getActions = () => {
    let actions = [
      {
        name: settingsMatch ? "Save" : "Save & Continue",
        isDisabled: !isValid,
        action: save,
        type: "black",
      },
      { name: "cancel", action: leftNavigation, type: "grey" },
    ];
    if (settingsMatch && deletable && isOrganizationUser(currentUser, currentTournament?.organization)) {
      actions.push({
        name: "Delete",
        action: deleteTournament,
        type: "transparent red",
      });
    }
    return actions;
  };

  const leftNavigation = () => {
    if (isValid && !bannerNotifications) {
      setUnsaved(true);
      return;
    }
    dispatch(clearUtilityNotifications());
    dispatch(setTournamentNotifications([]));
    if (props?.quickStart) {
      navigate("/tournaments");
      return;
    }
    navigate(-1);
  };

  const getNavigation = () => {
    return {
      title: "Tournament Details",
      leftIcon: faChevronLeft,
      leftButtonClick: leftNavigation,
    };
  };
  const timeoutAction = () => {
    dispatch(setTournamentNotifications([]));
    dispatch(clearUtilityNotifications());
  };

  const getContent = () => {
    if (props?.quickStart === true) {
      return (
        <GSForm
          formTitle={"Quick Start"}
          formSections={quickStartForm(currentTournament, context)}
        ></GSForm>
      );
    }
    let title = "Create Tournament";
    if (settingsMatch) {
      title = "Edit Tournament";
    }

    return (
      <GSForm
        formTitle={title}
        formSections={getFormSections(currentTournament, context)}
      ></GSForm>
    );
  };
  const getDrawer = () => {
    return {
      actions: getActions(),
    };
  };
  let notificationSettings = {
    notifications: [...tournamentNotifications, ...utilNotifications],
    saveAction: save,
    bannerActions: getSaveBannerActions(save, leftNavigation),
    timeoutAction,
  };

  const [bannerNotifications, setUnsaved] = useNotificationBanner(
    notificationSettings
  );
  return (
    <tournament-create-edit>
      <GSSidePanelPage
        header={getNavigation()}
        banner={bannerNotifications}
        content={getContent()}
        drawer={getDrawer()}
      ></GSSidePanelPage>
    </tournament-create-edit>
  );
};

export default TournamentCreateEdit;
